import { PageBackLink } from "@/components/page-back-link"
import { ListSearchFilters } from "@/components/list-search-filters"
import { Pagination } from "@/components/pagination"
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { DataGridColumn } from "@/components/data-grid/data-grid"
import { StatusBadge } from '@/components/status-badge'
import { labStatusVariants } from '@/features/labs/lab-status'
import { DataGrid } from '@/components/data-grid'
import { ErrorBanner, ErrorBoundary } from '@/components/error-boundary'
import { PageBreadcrumbs, PageHeader } from '@/components/page-header'
import { queryKeys } from '@/lib/query-keys'
import { api } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { getRowIndexProps } from '@/lib/pagination'
import { LabFiltersSchema, LabStatusSchema, type Lab, type PaginationMeta } from '@optimist/schemas'

/**
 * Labs Queue page
 * Purpose: Review and unblock lab results
 */
export const Route = createFileRoute('/dashboard/labs/')({
  component: LabsPage,
  validateSearch: (search) => LabFiltersSchema.parse(search),
})

export function LabsPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const filters = Route.useSearch()
  const { page, limit } = filters

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.labs.list(filters),
    queryFn: () =>
      api.get<{ data: Lab[]; meta: PaginationMeta }>('/labs', {
        params: filters,
      }),
  })

  const onRowClicked = (row: Lab) => {
    navigate({ to: '/dashboard/labs/$labId', params: { labId: row.id } })
  }

  const columnDefs: DataGridColumn<Lab>[] = [
      {
        field: 'id',
        headerName: 'Lab ID',
      },
      {
        field: 'patientName',
        headerName: 'Patient',
        render: (row) => row.patientName || '-',
      },
      {
        field: 'status',
        headerName: 'Status',
        render: (row) => {
          const status = row.status
          if (!status) return null
          return (
            <StatusBadge variant={labStatusVariants[status]}>
              {status.replace(/_/g, ' ')}
            </StatusBadge>
          )
        },
      },
      {
        field: 'vendorCaseStatus',
        headerName: 'Integration',
        render: (row) => row.vendorCaseStatus ? String(row.vendorCaseStatus).replace(/_/g, ' ') : '-',
      },
      {
        field: 'daysWaiting',
        headerName: 'Days Waiting',
        render: (row) => row.daysWaiting !== undefined ? `${row.daysWaiting}d` : '-',
      },
      {
        field: 'orderedAt',
        headerName: 'Ordered',
        render: (row) => row.orderedAt ? formatDateTime(row.orderedAt) : '-',
      },
      {
        field: 'lastEventAt',
        headerName: 'Last Event',
        render: (row) => row.lastEventAt ? formatDateTime(row.lastEventAt) : '-',
      },
      {
        field: 'hasFailures',
        headerName: 'Failures',
        render: (row) => (row.hasFailures ? 'Yes' : 'No'),
      },
    ]

  const labs = data ? data.data : []
  const meta = data ? data.meta : null
  const rowIndexProps = getRowIndexProps({ meta, page, limit, mode: 'descending' })
  const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
  const handleRetry = () => {
    void refetch()
  }

  return (
    <ErrorBoundary
      title="Labs page error"
      fallback={<ErrorBanner title="Labs unavailable" message="Something went wrong loading this page." />}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <PageHeader back={<PageBackLink to="/dashboard" label="Back to dashboard" />}
            breadcrumbs={
              <PageBreadcrumbs
                items={[
                  <Link key="dashboard" to="/dashboard">Dashboard</Link>,
                  <span key="labs">Labs</span>,
                ]}
              />
            }
            title="Labs"
          />

        </div>

        <ListSearchFilters
          key={JSON.stringify([filters.search, filters.status])}
          title="Search and filter labs"
          placeholder="Lab ID, order ID, or patient name"
          search={filters.search}
          status={filters.status}
          statuses={LabStatusSchema.options.map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ") }))}
          onApply={(next) => navigate({ search: LabFiltersSchema.parse({ ...next, page: 1, limit }) })}
          onClear={() => navigate({ search: { page: 1, limit } })}
        />

        {error ? (
          <ErrorBanner title="Error loading labs" message={errorMessage} onRetry={handleRetry} />
        ) : null}

        <ErrorBoundary
          resetKey={labs.length}
          fallback={<ErrorBanner title="Labs table failed" message="Unable to render lab data." />}
        >
          <DataGrid<Lab>
            rowData={labs}
            columnDefs={columnDefs}
            loading={isLoading}
            {...rowIndexProps}
            onRowClicked={onRowClicked}
          />
        </ErrorBoundary>

        {meta ? (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.totalPages}
            onPageChange={(nextPage) => navigate({ search: (prev) => ({ ...prev, page: nextPage }) })}
            onLimitChange={(nextLimit) => navigate({ search: (prev) => ({ ...prev, page: 1, limit: nextLimit }) })}
          />
        ) : null}
      </div>
    </ErrorBoundary>
  )
}
